import { SignOutButton } from '@clerk/remix'
import {
	Menu,
	MenuButton,
	MenuItem,
	MenuItems,
	Transition,
} from '@headlessui/react'
import { Link } from '@remix-run/react'
import React, { useState } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { Icon } from '../../components/ui/icon'

export const AppHeader = ({
	companyName,
	employeeName,
	role,
	showTrialLabel,
	numberOfTrialDaysRemaining,
}: {
	companyName?: string
	employeeName?: string
	role?: string
	showTrialLabel?: boolean
	numberOfTrialDaysRemaining?: number
}): JSX.Element => {
	const [isMenuOpen, setIsMenuOpen] = useState(false)

	return (
		<>
			<header className="fixed left-0 top-0 z-50 h-[48px] w-full bg-primary shadow">
				{/* Desktop menu */}
				<div className="absolute right-[16px] top-0 hidden items-center gap-[16px] md:inline-flex">
					<div className="relative inline-flex flex-[0_0_auto] items-start gap-[8px]"></div>
					<MenuUser employeeName={employeeName ?? ''} role={role ?? ''} />
					{employeeName && (
						<>
							<div className="relative h-[48px] w-[2px] bg-[#fdfdfd]" />
							<SignOutButton>
								<div className="relative inline-flex flex-[0_0_auto] cursor-pointer items-center gap-[4px]">
									<img
										className="relative h-[20px] w-[20px]"
										alt="Logout"
										src="/img/logout.svg"
									/>
									<div className="relative w-fit whitespace-nowrap text-right text-[12px] font-medium leading-[12px] tracking-[0] text-white [font-family:'YuGothic-Medium',Helvetica]">
										ログアウト
									</div>
								</div>
							</SignOutButton>
						</>
					)}
				</div>
				{/* Desktop logo and company info */}
				<div className="absolute left-[16px] top-[12px] hidden items-center gap-[16px] md:inline-flex">
					<Link to={`/`}>
						<img
							className="relative h-[24px] w-[116.76px]"
							alt="Logo"
							src="/img/logo.svg"
						/>
					</Link>
					<div className="relative w-fit whitespace-nowrap text-[12px] font-medium leading-[12px] tracking-[0] text-white">
						{companyName}
					</div>
					{showTrialLabel && (
						<div className="relative w-fit">
							{numberOfTrialDaysRemaining === 0 ? (
								<EndTrialLabel />
							) : (
								<DuaringTrialLabel
									numberOfTrialDaysRemaining={numberOfTrialDaysRemaining ?? 0}
								/>
							)}
						</div>
					)}
				</div>

				{/* Mobile header */}
				<div className="flex items-center justify-between px-4 py-3 md:hidden">
					<Link to={`/`}>
						<img className="h-[20px] w-auto" alt="Logo" src="/img/logo.svg" />
					</Link>
					<button
						type="button"
						className="text-white"
						onClick={() => setIsMenuOpen(!isMenuOpen)}
					>
						<svg
							className="h-6 w-6"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							{isMenuOpen ? (
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							) : (
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 6h16M4 12h16M4 18h16"
								/>
							)}
						</svg>
					</button>
				</div>
			</header>

			{/* Mobile menu */}
			{isMenuOpen && (
				<div className="fixed left-0 right-0 top-[48px] z-40 bg-primary/95 shadow-md md:hidden">
					<div className="px-4 py-2">
						{companyName && (
							<div className="mb-3 text-sm font-medium text-white">
								{companyName}
							</div>
						)}
						{showTrialLabel && (
							<div className="mb-3">
								{numberOfTrialDaysRemaining === 0 ? (
									<EndTrialLabel />
								) : (
									<DuaringTrialLabel
										numberOfTrialDaysRemaining={numberOfTrialDaysRemaining ?? 0}
									/>
								)}
							</div>
						)}
						{employeeName && (
							<>
								<div className="mb-3 text-base font-medium text-white">
									{employeeName}
								</div>
								<div className="space-y-2">
									<Link
										to="reset-password"
										className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-white hover:bg-white/10"
										onClick={() => setIsMenuOpen(false)}
									>
										<Icon name="lock-reset" size="sm" fill="white" />
										パスワード変更
									</Link>
									{role === 'admin' && (
										<Link
											to="settings/admin"
											className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-white hover:bg-white/10"
											onClick={() => setIsMenuOpen(false)}
										>
											<Icon name="cog-8-tooth" size="sm" fill="white" />
											管理者設定
										</Link>
									)}
									<div className="mt-3 border-t border-white/20 pt-3">
										<SignOutButton>
											<button className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-white hover:bg-white/10">
												<img
													className="h-[16px] w-[16px]"
													alt="Logout"
													src="/img/logout.svg"
												/>
												ログアウト
											</button>
										</SignOutButton>
									</div>
								</div>
							</>
						)}
					</div>
				</div>
			)}
		</>
	)
}
const MenuUser = ({
	employeeName,
	role,
}: {
	employeeName: string
	role: string
}) => {
	return (
		<div className="text-right">
			<Menu as="div" className="inline-block text-left">
				<div>
					<MenuButton className="inline-flex w-full justify-center  px-4 py-2 text-sm font-medium text-white ">
						<div className="relative inline-flex flex-[0_0_auto] items-start gap-[8px]">
							<div className="relative mt-[-1.00px] w-fit whitespace-nowrap text-right text-[16px] font-medium leading-[16px] tracking-[0] text-white">
								{employeeName}
							</div>
						</div>
					</MenuButton>
				</div>
				<Transition
					as={Fragment}
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
				>
					<MenuItems className="absolute mt-2 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
						<div className="px-1 py-1">
							<MenuItem>
								{({ active }) => (
									<Link to={`reset-password`}>
										<button
											className={`${
												active ? 'bg-[#068CB7] text-white' : 'text-gray-900'
											} group flex w-full items-center rounded-md px-2 py-2 text-sm`}
										>
											<div className="flex items-center gap-2">
												<Icon name="lock-reset" size="sm" fill="black" />
												パスワード変更
											</div>
										</button>
									</Link>
								)}
							</MenuItem>
							{role === 'admin' && (
								<MenuItem>
									{({ active }) => (
										<Link to={`settings/admin`}>
											<button
												className={`${
													active ? 'bg-[#068CB7] text-white' : 'text-gray-900'
												} group flex w-full items-center rounded-md px-2 py-2 text-sm`}
											>
												<div className="flex items-center gap-2">
													<Icon name="cog-8-tooth" size="sm" fill="black" />
													管理者設定
												</div>
											</button>
										</Link>
									)}
								</MenuItem>
							)}
						</div>
					</MenuItems>
				</Transition>
			</Menu>
		</div>
	)
}
const DuaringTrialLabel = ({
	numberOfTrialDaysRemaining,
}: {
	numberOfTrialDaysRemaining: number
}) => (
	<div className="flex items-center justify-center gap-2.5 bg-white px-2 py-1 text-xs font-light leading-[100%] text-[#707070]">
		無料トライアル：残り{numberOfTrialDaysRemaining}日
	</div>
)
const EndTrialLabel = () => (
	<div className="flex items-center justify-center gap-2.5 bg-[#d00707] px-2 py-1 text-xs font-light leading-[100%] text-white">
		無料トライアル：終了
	</div>
)

export const MainHeader = ({
	title,
	children,
}: {
	title: string
	children?: React.ReactNode
}) => (
	<header>
		<h1 className="py-2  text-2xl font-light leading-[100%] text-[#232323]">
			{title}
		</h1>
		<div className="w-full py-2  text-sm font-light leading-[180%] text-[#232323]">
			{children}
		</div>
	</header>
)
